import React from 'react';
import ReactDOM from 'react-dom';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet.locatecontrol';
import 'leaflet.markercluster';
import './leaflet/L.Control.Camera';
import './leaflet/L.Control.Zoom';
import withContext from './withContext';
import PhotoSetThumbnailView from './PhotoSetThumbnailView';
import markerPhoto from './images/markers/photo.svg';
import markerPhotoSet from './images/markers/photo-set.svg';

class MapView extends React.PureComponent {

    constructor(props) {

        super(props);

        this.mapPhotoSets = new Map();

        this.mapRef = React.createRef();

        this.handleUrlReset = this.handleUrlReset.bind(this);
        this.handleBoundsChange = this.handleBoundsChange.bind(this);
        this.handleUserLocationChange = this.handleUserLocationChange.bind(this);
        this.handleMarkerClick = this.handleMarkerClick.bind(this);
        this.handlePhotoSetViewOpen = this.handlePhotoSetViewOpen.bind(this);
        this.handleMapContentChange = this.handleMapContentChange.bind(this);

    }

    componentDidMount() {

        this.map = L.map(this.mapRef.current, {

                attributionControl: false,
                zoomControl: false,
            })
            .setView([52.0714306, 5.1047153], 4); // Forget The Fish office.

        this.map.on('moveend', this.handleBoundsChange);
        this.map.on('click', this.handleUrlReset);
        this.map.on('locationfound', this.handleUserLocationChange);

        L.control.attribution({position: 'bottomleft'}).addTo(this.map);
        L.control.zoom({position: 'bottomright'}).addTo(this.map);

        L.control.locate({

            createButtonCallback: function(container, options) {
                const link = L.DomUtil.create('a', 'leaflet-bar-part leaflet-bar-part-single leaflet-font-awesome', container);

                link.tabIndex = 0;
                link.title = options.strings.title;

                const icon = L.DomUtil.create(options.iconElementTag, options.icon, link);

                return {link: link, icon: icon};
            },
            drawCircle: false,
            flyTo: true,
            icon: 'fas fa-crosshairs',
            iconLoading: 'fas fa-spinner fa-spin',
            position: 'bottomright',

        }).addTo(this.map);

        if (this.props.cameraControl) {

            L.control.camera({

                onClick: () => {

                    this.props.history.push('/camera');
                },
                position: 'bottomright'

            }).addTo(this.map);
        }

        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {

            attribution: '© <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>, © <a href="https://cartodb.com/attributions">Carto</a>',

        }).addTo(this.map);

        this.markerClusterGroup = L.markerClusterGroup().addTo(this.map);

        this.displayPhotoSets();
        this.goToActivePhotoSet({animate: false});
    }

    componentDidUpdate(prevProps) {

        if (this.props.photoSets !== prevProps.photoSets)
            this.displayPhotoSets();

        if (this.props.activePhotoSet !== prevProps.activePhotoSet)
            this.goToActivePhotoSet();
    }

    componentWillUnmount() {

        this.map.remove();
    }

    displayPhotoSets() {

        // Remove old ones.

        this.mapPhotoSets.forEach((entry, set) => {

            if (!this.props.photoSets.includes(set)) {

                this.markerClusterGroup.removeLayer(entry.marker);
                this.mapPhotoSets.delete(set);
            }
        });

        // Add new ones.

        const photoIcon = L.icon({

            iconUrl: markerPhoto,
            iconSize: [21, 21], // Specifying the size will correctly center the icon and popup anchor.
        });

        const photoSetIcon = L.icon({

            iconUrl: markerPhotoSet,
            iconSize: [33, 33], // Specifying the size will correctly center the icon and popup anchor.
        });

        this.props.photoSets.filter(set => !this.mapPhotoSets.has(set)).forEach(set => {

            const marker = L.marker([set.latitude, set.longitude], {icon: set.photos.length > 1 ? photoSetIcon : photoIcon}).addTo(this.markerClusterGroup);

            marker.on('click', () => {this.handleMarkerClick(set);});

            marker.on('popupclose', () => {

               if (marker.getPopup()) {

                    ReactDOM.unmountComponentAtNode(marker.getPopup().getContent());
                    marker.unbindPopup();

                }
            });

            this.mapPhotoSets.set(set, {marker});
        });

        this.handleMapContentChange();
    }

    getPhotoSetPopup(set) {

        const container = document.createElement('div'); // React needs a container to render components in.

        const popup = L.popup({

                className: 'map-view-popup',
                closeButton: false,
                maxWidth: Infinity,
                minWidth: 0,
            })
            .setLatLng([set.latitude, set.longitude])
            .setContent(container);

        const ViewWithContext = withContext(PhotoSetThumbnailView, this.context); // Pass the required router context.

        ReactDOM.render(<ViewWithContext isPhotoLocationNearUserLocation={this.props.isPhotoLocationNearUserLocation} photoSet={set} showControls={true} onFullScreen={() => this.handlePhotoSetViewOpen(set)}/>, container, () => {

            // Unfortunately, if the image is not loaded yet in browser cache, the popup will open right away with default size
            // and adjust its size but not its position once the image is fully loaded and displayed. As a result, the popup is
            // shifted and its arrow is misplaced compared to the marker it is bound to.
            //
            // See also: https://github.com/Leaflet/Leaflet/issues/5484

            container.querySelector('img').addEventListener('load', (e) => {

                popup.update();
            });
        });

        return popup;
    }

    goToActivePhotoSet(options) {

        if (this.props.activePhotoSet)
            this.goToPhotoSet(this.props.activePhotoSet, options);
    }

    goToPhotoSet(photoSet, options) {

        // this.map.flyTo([photoSet.latitude, photoSet.longitude], Math.max(this.map.getZoom(), 10), options);
        // this.map.once('moveend', () => {});

        if (this.mapPhotoSets.has(photoSet)) {

            const marker = this.mapPhotoSets.get(photoSet).marker;

            this.markerClusterGroup.zoomToShowLayer(marker, () => {

                const popup = this.getPhotoSetPopup(photoSet);
                this.mapPhotoSets.get(photoSet).marker.bindPopup(popup).openPopup();
            });
        }
    }
  
    handleUrlReset() {
      this.props.history.push("/");
    }

    handleMapContentChange() {
        const bounds = this.map.getBounds();
        let visiblePhotoSets = [];
        this.props.photoSets.forEach((photoSet, i) => {
            if (bounds.contains([photoSet.latitude, photoSet.longitude]))
                visiblePhotoSets[i] = photoSet;
        });

        this.props.onMapContentChange &&
            this.props.onMapContentChange(visiblePhotoSets);
    }

    handleBoundsChange() {
        this.handleMapContentChange();
        if (this.props.onBoundsChange)
            this.props.onBoundsChange(this.map.getBounds());
    }

    handleUserLocationChange(e) {
        this.props.onUserLocationChange(e);
    }

    handleMarkerClick(photoSet) {

        if (this.props.onActivePhotoSetChange)
            this.props.onActivePhotoSetChange(photoSet);
    }

    handlePhotoSetViewOpen(photoSet) {

        if (this.props.onPhotoSetViewOpen)
            this.props.onPhotoSetViewOpen(photoSet);
    }
  
    render() {

        return (

            <div ref={this.mapRef} className="map-view" style={this.props.style}/>
        );
    }

    static contextTypes = {

        router: PropTypes.object.isRequired, // Require the router context so it can be passed on to popups.
    }
}

MapView.defaultProps = {

    cameraControl: true,
    photoSets: [],
};

export default withRouter(MapView);



// WEBPACK FOOTER //
// ./src/components/MapView.js